import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { consumerApi } from 'lib/consumer-api';
import { API_ENDPOINTS } from 'constants/endpoints';
import { Users, Search, Eye } from 'lucide-react';
import ConsumerListPageLayout from 'components/consumer/ConsumerListPageLayout';
import {
  ConsumerFilterField,
  consumerFilterInputClass,
  consumerFilterInputMonoClass,
} from 'components/consumer/ConsumerFilterBar';
import {
  CONSUMER_TD_CLASS,
  CONSUMER_TH_CLASS,
  CONSUMER_TH_ACTION_CLASS,
  CONSUMER_EYE_BTN_CLASS,
} from 'components/consumer/consumer-list-styles';
import { appendCursorParams, parseCursorPagination } from 'lib/cursor-pagination';
import { useCursorListPage } from 'hooks/useCursorListPage';
import type { ConsumerPortalUserRow } from 'types/consumer';

type UserFilters = {
  username: string;
  displayName: string;
  externalId: string;
  status: '' | 'active' | 'suspended' | '';
};

const emptyFilters: UserFilters = { username: '', displayName: '', externalId: '', status: '' };

const countFilters = (f: UserFilters) =>
  (f.username.trim() ? 1 : 0) +
  (f.displayName.trim() ? 1 : 0) +
  (f.externalId.trim() ? 1 : 0) +
  (f.status ? 1 : 0);

function buildFilterParams(filters: UserFilters) {
  const params = new URLSearchParams();
  if (filters.username.trim()) params.set('username', filters.username.trim());
  if (filters.displayName.trim()) params.set('displayName', filters.displayName.trim());
  if (filters.externalId.trim()) params.set('externalId', filters.externalId.trim());
  if (filters.status) params.set('status', filters.status);
  return params;
}

const ConsumerUsers = () => {
  const navigate = useNavigate();
  const [draftFilters, setDraftFilters] = useState<UserFilters>(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState<UserFilters>(emptyFilters);

  const fetchPage = useCallback(async (cursor: number | null) => {
    const params = appendCursorParams(buildFilterParams(appliedFilters), cursor);
    const res = await consumerApi.get<ConsumerPortalUserRow[]>(
      `${API_ENDPOINTS.CONSUMER_PORTAL.USERS}?${params.toString()}`
    );
    const meta = parseCursorPagination(res);
    return {
      items: res.success && Array.isArray(res.data) ? res.data : [],
      nextCursor: meta.nextCursor,
      hasMore: meta.hasMore,
      total: meta.total,
      totalPages: meta.totalPages,
    };
  }, [appliedFilters]);

  const {
    page,
    items,
    loading,
    refreshing,
    total,
    totalPages,
    limit,
    resetAndLoad,
    refreshCurrent,
    goFirst,
    goToPage,
    goLast,
  } = useCursorListPage(fetchPage);

  useEffect(() => {
    resetAndLoad();
  }, [appliedFilters, resetAndLoad]);

  const isDatasetEmpty = total === 0 && !loading && countFilters(appliedFilters) === 0;
  const isFilteredEmpty = items.length === 0 && !loading && countFilters(appliedFilters) > 0;

  return (
    <ConsumerListPageLayout
      icon={Users}
      title="Players"
      description="Platform players linked to your consumer only."
      totalCount={null}
      filteredCount={items.length}
      loading={loading}
      refreshing={refreshing}
      onRefresh={() => refreshCurrent()}
      activeFilterCount={countFilters(appliedFilters)}
      draftFilterCount={countFilters(draftFilters)}
      onApplyFilters={() => setAppliedFilters({ ...draftFilters })}
      onClearFilters={() => {
        setDraftFilters(emptyFilters);
        setAppliedFilters(emptyFilters);
      }}
      filterFields={
        <>
          <ConsumerFilterField label="Username">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={15} />
              <input
                type="text"
                placeholder="Search username"
                value={draftFilters.username}
                onChange={(e) => setDraftFilters((p) => ({ ...p, username: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && setAppliedFilters({ ...draftFilters })}
                className={`${consumerFilterInputClass} pl-9`}
              />
            </div>
          </ConsumerFilterField>
          <ConsumerFilterField label="Display name">
            <input
              type="text"
              placeholder="Search display name"
              value={draftFilters.displayName}
              onChange={(e) => setDraftFilters((p) => ({ ...p, displayName: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && setAppliedFilters({ ...draftFilters })}
              className={consumerFilterInputClass}
            />
          </ConsumerFilterField>
          <ConsumerFilterField label="External ID">
            <input
              type="text"
              placeholder="e.g. U10045"
              value={draftFilters.externalId}
              onChange={(e) => setDraftFilters((p) => ({ ...p, externalId: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && setAppliedFilters({ ...draftFilters })}
              className={consumerFilterInputMonoClass}
            />
          </ConsumerFilterField>
          <ConsumerFilterField label="Status">
            <select
              value={draftFilters.status}
              onChange={(e) => setDraftFilters((p) => ({ ...p, status: e.target.value as UserFilters['status'] }))}
              className={consumerFilterInputClass}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </ConsumerFilterField>
        </>
      }
      tableTitle="Consumer Players"
      tableSubtitle="Players registered under your consumer"
      emptyIcon={Users}
      emptyTitle="No players yet"
      emptyDescription="No platform players are linked to your consumer."
      isDatasetEmpty={isDatasetEmpty}
      isFilteredEmpty={isFilteredEmpty}
      colSpan={5}
      page={page}
      itemCount={items.length}
      limit={limit}
      total={total}
      totalPages={totalPages}
      onFirst={goFirst}
      onPageChange={goToPage}
      onLast={goLast}
      tableHead={
        <tr>
          <th className={CONSUMER_TH_CLASS}>Username</th>
          <th className={CONSUMER_TH_CLASS}>Display Name</th>
          <th className={CONSUMER_TH_CLASS}>External ID</th>
          <th className={CONSUMER_TH_CLASS}>Status</th>
          <th className={CONSUMER_TH_ACTION_CLASS}>Action</th>
        </tr>
      }
      tableBody={
        <>
          {items.map((u) => (
            <tr key={u.id} className="hover:bg-subtle/40 transition-colors">
              <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}>
                <span className="text-sm font-semibold text-heading">{u.username}</span>
              </td>
              <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}>
                <span className="text-sm text-body">{u.displayName || '—'}</span>
              </td>
              <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}>
                <span className="font-mono text-xs text-muted">{u.externalUserId || '—'}</span>
              </td>
              <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}>
                <span
                  className={`px-2 py-0.5 text-[10px] font-bold rounded-md uppercase ${
                    u.status === 1 ? 'bg-emerald-500/10 text-emerald-700' : 'bg-red-500/10 text-red-700'
                  }`}
                >
                  {u.status === 1 ? 'Active' : 'Suspended'}
                </span>
              </td>
              <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}>
                <button
                  type="button"
                  onClick={() => navigate(`/consumer/users/${u.id}`)}
                  title="View user"
                  aria-label="View user"
                  className={CONSUMER_EYE_BTN_CLASS}
                >
                  <Eye size={16} strokeWidth={2} />
                </button>
              </td>
            </tr>
          ))}
        </>
      }
    />
  );
};

export default ConsumerUsers;
